import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'

// Tela "Importar CT-e" — equivalente web do WIMPCTE/PIMPCTE legado, mesmo
// espírito assistido da ImportarNFe.tsx: não grava nada sozinha, só destranca
// a porta de entrada (chave já em frota.ntvxml, ou upload de um XML que nunca
// passou pela Distribuição DF-e) e manda pro Lançamento de CT-e 57 pré-preencher
// a tela pra revisão — mesmo fluxo que o botão "Importar XML" do Manifestador
// de CT-e já usa.

const MODELOS_VALIDOS = ['57', '67']

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.85rem', height: 32, background: '#fff',
}
const label: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }

function Campo({ rotulo, width, children }: { rotulo: string; width?: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

export default function ImportarCTe() {
  const { empresaCod } = useEmpresa()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [chave, setChave] = useState('')
  const [buscandoChave, setBuscandoChave] = useState(false)
  const [erroChave, setErroChave] = useState('')

  const [arquivoNome, setArquivoNome] = useState('')
  const [enviandoArquivo, setEnviandoArquivo] = useState(false)
  const [erroArquivo, setErroArquivo] = useState('')

  function importarPorChave() {
    const digitos = chave.replace(/\D/g, '')
    if (digitos.length !== 44) { setErroChave('Chave inválida — precisa ter 44 dígitos.'); return }
    const modelo = digitos.slice(20, 22)
    if (!MODELOS_VALIDOS.includes(modelo)) {
      setErroChave(`Essa chave é modelo ${modelo}, não CT-e.`)
      return
    }
    setErroChave('')
    setBuscandoChave(true)
    navigate(`/compras/recebimentos/lancamento-cte57?importarChave=${digitos}`)
  }

  function selecionarArquivo() {
    fileInputRef.current?.click()
  }

  function aoSelecionarArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0]
    e.target.value = ''
    if (!arquivo) return
    setErroArquivo('')
    setArquivoNome(arquivo.name)
    setEnviandoArquivo(true)

    const leitor = new FileReader()
    leitor.onload = async () => {
      try {
        const xml = String(leitor.result || '')
        const r = await api.post('/erp/notas-entrada/importar-arquivo', { xml, empcod: empresaCod ?? 1, tipo: 'cte' })
        navigate(`/compras/recebimentos/lancamento-cte57?importarChave=${r.data.chave}`)
      } catch (err: any) {
        setErroArquivo(err?.response?.data?.error || 'Erro ao importar o arquivo XML.')
        setEnviandoArquivo(false)
      }
    }
    leitor.onerror = () => {
      setErroArquivo('Não foi possível ler o arquivo selecionado.')
      setEnviandoArquivo(false)
    }
    leitor.readAsText(arquivo, 'utf-8')
  }

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>Importar CT-e</h2>
      <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 1rem' }}>
        Importa um CT-e de frete pra revisão no Lançamento de CT-e 57 — igual o botão "Importar XML" do Manifestador
        de CT-e, só que você pode digitar a chave direto aqui, ou subir o arquivo XML de um conhecimento que ainda
        não passou pela Distribuição DF-e.
      </p>

      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem', marginBottom: '1rem',
      }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a6ea8', marginBottom: '0.75rem' }}>
          Já tenho a chave
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Campo rotulo="Chave CT-e" width={420}>
            <input style={{ ...inp, fontFamily: 'monospace' }} value={chave} maxLength={44}
              onChange={e => setChave(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && importarPorChave()}
              placeholder="44 dígitos" />
          </Campo>
          <button className="btn btn-primary btn-sm" disabled={buscandoChave} onClick={importarPorChave} style={{ height: 32 }}>
            {buscandoChave ? 'Importando...' : 'Importar'}
          </button>
        </div>
        {erroChave && <div className="alert alert-danger" style={{ marginTop: '0.75rem' }}>{erroChave}</div>}
        <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.5rem' }}>
          Só funciona se o conhecimento já estiver na Distribuição DF-e (já apareceu no Manifestador de CT-e).
        </div>
      </div>

      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem',
      }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a6ea8', marginBottom: '0.75rem' }}>
          Ou subir o arquivo XML
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input ref={fileInputRef} type="file" accept=".xml" onChange={aoSelecionarArquivo} style={{ display: 'none' }} />
          <button className="btn btn-primary btn-sm" disabled={enviandoArquivo} onClick={selecionarArquivo} style={{ height: 32 }}>
            {enviandoArquivo ? 'Importando...' : 'Selecionar arquivo...'}
          </button>
          {arquivoNome && <span style={{ fontSize: '0.8rem', color: '#374151' }}>{arquivoNome}</span>}
        </div>
        {erroArquivo && <div className="alert alert-danger" style={{ marginTop: '0.75rem' }}>{erroArquivo}</div>}
        <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.5rem' }}>
          Use quando o XML veio de outro lugar (baixado do portal da SEFAZ, recebido por e-mail, WhatsApp etc.) e o
          conhecimento nunca apareceu no Manifestador.
        </div>
      </div>
    </div>
  )
}
